import globalCacheManager from './globalCacheManager';

const CACHE_KEY = 'item';
const STORAGE_KEY = 'inventory_item_list_cache';

const loadCacheFromStorage = () => {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

const saveCacheToStorage = (data) => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
        // ignore storage errors
    }
};

class ItemCache {
  constructor() {
    this.data = loadCacheFromStorage();
    this.pendingPromise = null;
    this.lastFetchTime = null;
    this.isBulkUpdated = false; // Bundle로 업데이트되었는지 여부
  }

  /**
   * 아이템 목록 가져오기
   * @param {boolean} forceRefresh - 강제 새로고침 여부
   * @returns {Promise<Array>} 아이템 목록
   */
  async fetchData(forceRefresh = false) {
    // 페이지 새로고침 중이면 Bundle 분배 대기 — 개별 호출 차단
    if (globalCacheManager.isRefreshing) {
      return this.data || [];
    }

    // 캐시가 있고 강제 새로고침이 아니면 캐시 반환
    if (!forceRefresh && this.data) {
      return this.data;
    }

    // 이미 진행 중인 요청이 있으면 그것을 반환
    if (this.pendingPromise && !forceRefresh) {
      return this.pendingPromise;
    }

    // 새로운 요청 시작
    this.pendingPromise = (async () => {
      try {
        await globalCacheManager.refreshFromBundle(forceRefresh);
        return this.data || [];
      } catch (error) {
        console.error('[ItemCache] Error refreshing from bundle:', error);
        return this.data || [];
      } finally {
        this.pendingPromise = null;
      }
    })();

    return this.pendingPromise;
  }

  /**
   * 캐시된 아이템 목록 가져오기 (동기)
   * @returns {Array|null} 아이템 목록 또는 null
   */
  getData() {
    return this.data;
  }

  /**
   * 데이터 직접 설정 (Bundle 분배)
   * @param {Array} data - 아이템 목록
   */
  setData(data) {
    this.data = data || [];
    this.lastFetchTime = Date.now();
    this.isBulkUpdated = true; // Bundle로 업데이트됨
    saveCacheToStorage(this.data);
  }

  /**
   * 캐시 무효화
   */
  invalidate() {
    this.data = null;
    this.pendingPromise = null;
    this.lastFetchTime = null;
    this.isBulkUpdated = false;
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * 마지막 갱신 시간 가져오기
   * @returns {number|null} 마지막 갱신 시간 (타임스탬프)
   */
  getLastFetchTime() {
    return this.lastFetchTime;
  }
}

// 싱글톤 인스턴스
const itemCache = new ItemCache();

// 전역 캐시 매니저에 등록
globalCacheManager.register(CACHE_KEY, itemCache);

// 기존 함수들 유지 (호환성)
export const fetchItemListCached = async (forceRefresh = false) => {
  return await itemCache.fetchData(forceRefresh);
};

export const invalidateItemListCache = () => {
  itemCache.invalidate();
};

/**
 * 캐시된 아이템 목록 가져오기 (동기)
 * @returns {Array|null} 아이템 목록 또는 null
 */
export const getItemListCached = () => {
  return itemCache.getData();
};

export default itemCache;

